import React, { useState } from 'react';
import Header from './Home/components/Header';
import Footer from './Home/components/Footer';
import { ArrowUpTrayIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { WebIO } from '@gltf-transform/core';
import { Link } from '@tanstack/react-router';
import { useSEO } from '../hooks/useSEO';
import RelatedGuides from '../components/RelatedGuides';

// A .gltf usually references external .bin and texture files by relative URI.
// Match them to the selected files by path or, failing that, by file name.
const findResource = (files, uri) => {
  const decoded = decodeURIComponent(uri).replace(/^\.\//, '');
  const name = decoded.split('/').pop();
  return files.find((f) => f.name === decoded || f.name === name);
};

function GltfToGlbUpload() {
  const [file, setFile] = useState(null);
  const [resourceFiles, setResourceFiles] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [converting, setConverting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  const handleFiles = (fileList) => {
    setSuccessMessage('');

    const files = Array.from(fileList || []);
    if (files.length === 0) {
      setFile(null);
      setResourceFiles([]);
      setErrorMessage('');
      return;
    }

    const gltfFile = files.find((f) => f.name.toLowerCase().endsWith('.gltf'));
    if (!gltfFile) {
      setErrorMessage('Please select a .gltf file (plus its .bin and texture files, if it has any).');
      setFile(null);
      setResourceFiles([]);
      return;
    }

    const others = files.filter((f) => f !== gltfFile);
    const totalMB = files.reduce((sum, f) => sum + f.size, 0) / 1024 / 1024;
    setFile(gltfFile);
    setResourceFiles(others);
    setErrorMessage(
      `Selected: ${gltfFile.name}${others.length ? ` + ${others.length} resource file${others.length > 1 ? 's' : ''}` : ''} (${totalMB.toFixed(2)} MB)`
    );
  };

  const handleFileChange = (e) => {
    handleFiles(e.target.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const getState = () => {
    if (!file) return 'idle';
    if (!file.name.toLowerCase().endsWith('.gltf')) return 'error';
    if (errorMessage.startsWith('Missing')) return 'error';
    return 'valid';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return;

    setConverting(true);
    setSuccessMessage('');
    try {
      const json = JSON.parse(await file.text());

      const uris = [...(json.buffers || []), ...(json.images || [])]
        .map((entry) => entry.uri)
        .filter((uri) => uri && !uri.startsWith('data:'));

      const missing = uris.filter((uri) => !findResource(resourceFiles, uri));
      if (missing.length > 0) {
        setErrorMessage(`Missing files: ${missing.join(', ')}. Select them together with ${file.name}.`);
        return;
      }

      const resources = {};
      for (const uri of uris) {
        resources[uri] = new Uint8Array(await findResource(resourceFiles, uri).arrayBuffer());
      }

      const io = new WebIO();
      const doc = await io.readJSON({ json, resources });
      const glbUint8 = await io.writeBinary(doc);

      const blob = new Blob([glbUint8], { type: 'model/gltf-binary' });
      const url = URL.createObjectURL(blob);
      const downloadLink = document.createElement('a');
      const baseName = file.name.replace(/\.gltf$/i, '.glb');
      downloadLink.href = url;
      downloadLink.download = baseName;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(url);

      setSuccessMessage('Conversion complete! Your download should start automatically.');
      setFile(null);
      setResourceFiles([]);
      setErrorMessage('');
    } catch (err) {
      console.error(err);
      setErrorMessage('Failed to convert file. Ensure it is a valid GLTF.');
    } finally {
      setConverting(false);
    }
  };

  const state = getState();
  const isError = state === 'error';
  const isValid = state === 'valid';

  return (
    <section className="bg-background" id="file-upload">
      <div className="max-w-5xl w-full mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="lg:text-center">
          <h2 className="text-base text-accent font-semibold tracking-wide uppercase">
            Try it out
          </h2>
          <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-foreground sm:text-4xl">
            Convert GLTF to GLB in seconds
          </p>
          <p className="mt-4 max-w-2xl text-xl text-muted-foreground/90 lg:mx-auto">
            Free, instant, and entirely in-browser.
          </p>
        </div>

        <div className="mt-10">
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="space-y-8">
              <div className="mt-6 space-y-6">
                <div>
                  <label htmlFor="file-upload" className="block text-sm font-medium text-muted-foreground mb-2">
                    Upload your GLTF file
                  </label>
                  <div
                    className={`group flex justify-center rounded-xl border-2 border-dashed p-12 transition-all duration-200 cursor-pointer bg-background-elevated ${
                      isDragging ? 'border-accent bg-accent/10' : isError ? 'border-danger' : isValid ? 'border-success' : 'border-border/60 hover:border-border'
                    }`}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onClick={() => document.getElementById('gltf-upload-input').click()}
                  >
                    <div className="text-center pointer-events-none">
                      {!file && <ArrowUpTrayIcon className="mx-auto h-12 w-12 text-muted-foreground/70" />}
                      {isValid && <CheckCircleIcon className="mx-auto h-12 w-12 text-success" />}
                      {isError && <ExclamationCircleIcon className="mx-auto h-12 w-12 text-danger" />}
                      <div className="mt-4 flex text-sm leading-6 text-muted-foreground justify-center">
                        <label htmlFor="gltf-upload-input" className="relative cursor-pointer rounded-lg font-medium text-accent hover:text-accent/80 focus:outline-none pointer-events-auto transition-colors duration-200">
                          <span>Choose files</span>
                          <input id="gltf-upload-input" name="file-upload" type="file" className="sr-only" onChange={handleFileChange} accept=".gltf,.bin,.png,.jpg,.jpeg,.webp,.ktx2" multiple />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-muted-foreground/80 mt-2">Select the .gltf together with its .bin and texture files</p>
                      {errorMessage && (
                        <p className={`mt-2 text-sm ${isError ? 'text-danger' : 'text-success'}`}>{errorMessage}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-center">
              <button
                type="submit"
                className="inline-flex justify-center py-3 px-6 border border-transparent text-sm font-semibold rounded-lg text-primary-foreground bg-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent/50 transition-all duration-200 disabled:opacity-50 shadow-sm"
                disabled={!file || isError || converting}
              >
                {converting ? 'Converting…' : 'Convert now'}
              </button>
            </div>
          </form>
        </div>

        {successMessage && (
          <div className="mt-8 text-center">
            <div className="inline-block px-4 py-3 bg-success/10 border border-success/30 text-success rounded-xl shadow-sm">
              {successMessage}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export default function GltfToGlbPage() {
  useSEO({
    title: 'glTF to GLB Converter – Convert glTF to GLB Online, Free',
    description: 'Convert glTF to GLB online for free. Packs your .gltf, .bin and texture files into a single GLB right in your browser: no upload, no sign-up.',
    keywords: 'GLTF to GLB converter, 3D model conversion, GLB converter, GLTF converter, browser-based conversion',
    canonical: 'https://compress-glb.com/gltf-to-glb',
    ogImage: '/images/og/gltf-to-glb.png',
    ogTitle: 'glTF to GLB Converter – Convert glTF to GLB Online, Free',
    ogDescription: 'Pack your .gltf, .bin and texture files into a single GLB right in your browser. No upload, no sign-up.',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": "GLTF to GLB Converter",
      "description": "Free online tool to convert GLTF files to GLB format",
      "url": "https://compress-glb.com/gltf-to-glb/",
      "applicationCategory": "UtilityApplication",
      "operatingSystem": "Web Browser",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      }
    }
  });

  return (
    <div className="bg-background text-foreground min-h-screen flex flex-col">
      <Header />

      <main className="flex-grow">
        <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            <span className="block">GLTF to GLB Converter</span>
            <span className="block text-accent">Free & instant</span>
          </h1>
          <p className="mt-6 max-w-2xl mx-auto text-xl text-muted-foreground/90">
            Select your .gltf file together with its .bin and textures and download a single GLB. Everything runs locally in your browser, so nothing is uploaded.
          </p>
          <div className="mt-8 max-w-md mx-auto sm:flex sm:justify-center md:mt-8">
            <a href="#file-upload" className="w-full flex items-center justify-center px-4 py-3 border border-accent text-base font-medium rounded-lg text-accent hover:bg-accent/10 md:py-4 md:text-lg md:px-6 transition-all duration-200">
              Try for free
            </a>
          </div>
        </div>

        <GltfToGlbUpload />

        <section className="py-16 bg-background">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
            <div>
              <h2 className="text-2xl font-bold text-foreground">What is the difference between glTF and GLB?</h2>
              <p className="mt-3 text-muted-foreground/90">
                Both store the same glTF 2.0 scene. A <strong className="text-foreground">.gltf</strong> file is JSON that usually points to separate .bin geometry and image files,
                which is handy for editing but easy to break when files get separated. A <strong className="text-foreground">.glb</strong> packs the JSON, geometry and textures
                into one binary file, which is what three.js, Babylon.js, &lt;model-viewer&gt;, Shopify, Unity and most AR viewers expect.
              </p>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-foreground">Why did my conversion fail?</h2>
              <p className="mt-3 text-muted-foreground/90">
                Most failures come from missing resources: the .gltf references a .bin or texture that wasn't selected. The converter lists any missing files by name,
                so select them together with the .gltf and try again. To check a model for spec errors, run it through the{' '}
                <Link to="/gltf-validator" className="text-accent hover:text-accent/80">glTF validator</Link>.
              </p>
            </div>
            <div className="p-6 bg-background-elevated rounded-xl border border-border/60 text-center">
              <h2 className="text-2xl font-bold text-foreground">Next: make your GLB up to 90% smaller</h2>
              <p className="mt-3 text-muted-foreground/90">
                Converting doesn't shrink the model. Compress the GLB with Draco geometry compression and WebP/AVIF textures so it loads fast on the web and in AR.
              </p>
              <Link
                to="/"
                className="mt-6 inline-flex items-center justify-center px-5 py-3 text-base font-medium rounded-lg text-primary-foreground bg-primary hover:bg-primary-hover transition-all duration-200 shadow-sm"
              >
                Compress your GLB
              </Link>
            </div>
          </div>
        </section>

        <RelatedGuides
          className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-16"
          title="Related guides"
          slugs={['gltf-vs-glb', 'what-is-a-glb-file', 'reduce-glb-file-size']}
        />
      </main>

      <Footer />
    </div>
  );
} 